import React, { useState } from 'react';
import { useTranslation, Trans } from '../../i18n';
import { roundScore } from '../../utils';
import speakingModuleIllustration from '@public/images/speaking_module.png';
import globalStyles from '../../styles/global.module.css';
import SingleBarChart from '../../components/SingleBarChart';
import SectionWithHeader from '../../components/SectionWithHeader';
import QuestionsToAskModal from '../../components/QuestionsToAskModal';

interface SpeakingOverallConfidenceProps {
    confidenceScore?: number;
    userRole: 'recruiter' | 'candidate';
    isWeb: boolean;
}

const SpeakingOverallConfidence = (props: SpeakingOverallConfidenceProps) => {
    const { confidenceScore, userRole, isWeb } = props;
    const { t } = useTranslation();

    const interviewConfidenceScore = roundScore(confidenceScore);
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

    const interviewConfidenceExplanation = () => {
        let scoreLabel: string;
        if (interviewConfidenceScore >= 0 && interviewConfidenceScore < 40) {
            scoreLabel = 'GOOD';
        } else if (
            interviewConfidenceScore >= 40 &&
            interviewConfidenceScore < 60
        ) {
            scoreLabel = 'GREAT';
        } else if (
            interviewConfidenceScore >= 60 &&
            interviewConfidenceScore <= 100
        ) {
            scoreLabel = 'EXCELLENT';
        } else {
            scoreLabel = 'NO_SCORE';
        }

        if (scoreLabel === 'NO_SCORE') {
            return (
                <p
                    className={`${globalStyles.text} ${globalStyles.textNoAboveSpace}`}>
                    <Trans
                        i18nKey={`SPEAKING.OVERALL_CONFIDENCE.USER_SCORE.${scoreLabel}`}
                    />
                </p>
            );
        } else {
            return (
                <>
                    <p
                        className={`${globalStyles.text} ${globalStyles.textNoAboveSpace}`}>
                        <Trans
                            i18nKey={`SPEAKING.OVERALL_CONFIDENCE.USER_SCORE.${scoreLabel}.PARAGRAPH_1`}
                            components={{
                                bold: <strong />,
                                italic: <i />,
                                color: <span style={{ color: '#00A5AC' }} />,
                            }}
                        />
                    </p>
                    <p
                        className={`${globalStyles.text} ${globalStyles.textNoAboveSpace}`}>
                        <Trans
                            i18nKey={`SPEAKING.OVERALL_CONFIDENCE.USER_SCORE.${scoreLabel}.PARAGRAPH_2`}
                        />
                    </p>
                    <p
                        className={`${globalStyles.text} ${globalStyles.textNoAboveSpace}`}>
                        <Trans
                            i18nKey={`SPEAKING.OVERALL_CONFIDENCE.USER_SCORE.${scoreLabel}.PARAGRAPH_3`}
                        />
                    </p>
                </>
            );
        }
    };

    return (
        <SectionWithHeader
            title={t('SPEAKING.TITLE')}
            subTitle={t('SPEAKING.OVERALL_CONFIDENCE.TITLE')}
            headerImage={speakingModuleIllustration}
            hasHeader={!isWeb}
            handleInfoDetails={() => setShowInfoModal(true)}
            isWeb={isWeb}
            scoreChart={
                <>
                    {(!!interviewConfidenceScore ||
                        interviewConfidenceScore !== 0) && (
                        <SingleBarChart
                            value={interviewConfidenceScore}
                            max={100}
                            hasScoreValue={userRole === 'recruiter'}
                            hasScoreLabel={true}
                            hasUserIcon={false}
                            hasScoreCheckpoints={true}
                            resultLabels={{
                                low: t(
                                    'GENERAL_SINGLE_BAR_CHART.RESULT_LABEL.LOW',
                                ),
                                mid: t(
                                    'GENERAL_SINGLE_BAR_CHART.RESULT_LABEL.MID',
                                ),
                                high: t(
                                    'GENERAL_SINGLE_BAR_CHART.RESULT_LABEL.HIGH',
                                ),
                            }}
                        />
                    )}
                    {(!interviewConfidenceScore ||
                        interviewConfidenceScore === 0) && (
                        <div className={`${globalStyles.textNoScoreDisplay}`}>
                            <svg
                                width="24"
                                height="25"
                                viewBox="0 0 27 28"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <circle
                                    cx="13.5"
                                    cy="14.376"
                                    r="13.5"
                                    fill="#F2D04B"
                                />
                                <path
                                    d="M12.32 7.87598H15.28L14.78 17.236H12.8L12.32 7.87598ZM13.8 22.016C13.3333 22.016 12.9467 21.8693 12.64 21.576C12.3333 21.2693 12.18 20.896 12.18 20.456C12.18 20.016 12.3333 19.6493 12.64 19.356C12.9467 19.0626 13.3333 18.916 13.8 18.916C14.2533 18.916 14.6267 19.0626 14.92 19.356C15.2267 19.6493 15.38 20.016 15.38 20.456C15.38 20.896 15.2267 21.2693 14.92 21.576C14.6267 21.8693 14.2533 22.016 13.8 22.016Z"
                                    fill="white"
                                />
                            </svg>
                            <span
                                className={`${globalStyles.text} ${globalStyles.textPaddingTop} ${globalStyles.textNoBelowSpace} ${globalStyles.textNoAboveSpace}`}>
                                {t(`NO_SCORE_AVAILABLE`)}
                            </span>
                        </div>
                    )}
                </>
            }>
            <>
                {interviewConfidenceExplanation()}
                {showInfoModal && isWeb && (
                    <QuestionsToAskModal
                        title={t('GENERAL_SCORE_MEANING')}
                        descriptions={[
                            t('SPEAKING.OVERALL_CONFIDENCE.EXPLANATION_TITLE'),
                            t('SPEAKING.OVERALL_CONFIDENCE.EXPLANATION_2'),
                        ]}
                        closeScanDetails={() => setShowInfoModal(false)}
                    />
                )}
            </>
        </SectionWithHeader>
    );
};

export default SpeakingOverallConfidence;
